// Valley ambience — procedural wind (brown noise, low-passed) + sparse birdsong.
// Zero audio assets, zero copyright. Enabled only by user gesture (navbar toggle).
class Ambience {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.enabled = false;
    this.timer = null;
    this.observer = null;
  }

  ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      d[i] = last * 3.5;
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const lp = this.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 420;
    lp.Q.value = 0.7;
    this.master = this.ctx.createGain();
    this.master.gain.value = 0;
    src.connect(lp);
    lp.connect(this.master);
    this.master.connect(this.ctx.destination);
    src.start();
  }

  on() {
    this.ensure();
    if (!this.ctx) return;
    this.enabled = true;
    this.ctx.resume();
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(this.master.gain.value, t);
    this.master.gain.linearRampToValueAtTime(0.045, t + 2);
    this.scheduleBird();
    this.watchSections();
  }

  off() {
    this.enabled = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(this.master.gain.value, t);
    this.master.gain.linearRampToValueAtTime(0.0001, t + 0.8);
    setTimeout(() => {
      if (!this.enabled && this.ctx) this.ctx.suspend();
    }, 900);
  }

  swell() {
    if (!this.enabled || !this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(this.master.gain.value, t);
    this.master.gain.linearRampToValueAtTime(0.075, t + 1.2);
    this.master.gain.linearRampToValueAtTime(0.045, t + 3.4);
  }

  bird() {
    if (!this.ctx || !this.enabled) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    const f0 = 2100 + Math.random() * 900;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * 1.35, t + 0.09);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.9, t + 0.2);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.012, t + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + 0.26);
    if (Math.random() < 0.4) setTimeout(() => this.enabled && this.bird(), 350);
  }

  scheduleBird() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (!this.enabled) return;
      this.bird();
      this.scheduleBird();
    }, 5000 + Math.random() * 9000);
  }

  watchSections() {
    if (typeof IntersectionObserver === "undefined" || this.observer) return;
    const ids = ["home", "timeline", "schedule", "houses", "map", "quiz", "guestbook", "gallery"];
    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && this.swell()),
      { threshold: 0.35 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) this.observer.observe(el);
    });
  }

  // Immediate mute for page exit — no fade.
  hardStop() {
    this.enabled = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.ctx || !this.master) return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.value = 0;
    this.master.gain.setValueAtTime(0, t);
    this.ctx.suspend();
  }

  // Tab hidden — quick fade then suspend; resumed on return if still enabled.
  suspendForHidden() {
    if (!this.ctx || !this.master || this.ctx.state === "suspended") return;
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(this.master.gain.value, t);
    this.master.gain.linearRampToValueAtTime(0.0001, t + 0.3);
    setTimeout(() => {
      if (this.ctx && document.hidden) this.ctx.suspend();
    }, 350);
  }

  resumeForVisible() {
    if (!this.enabled || !this.ctx) return;
    this.ctx.resume();
    const t = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setValueAtTime(0.0001, t);
    this.master.gain.linearRampToValueAtTime(0.045, t + 1.2);
  }
}

export const ambience = new Ambience();
if (typeof window !== "undefined") {
  window.__ambience = ambience;

  const stopEverything = () => {
    ambience.hardStop();
    window.speechSynthesis?.cancel?.();
  };

  window.addEventListener("pagehide", stopEverything);
  window.addEventListener("beforeunload", stopEverything);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      ambience.suspendForHidden();
      window.speechSynthesis?.cancel?.();
    } else {
      ambience.resumeForVisible();
    }
  });
  window.addEventListener("pageshow", (e) => {
    if (e.persisted) ambience.resumeForVisible();
  });
}
